import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import Hero from '../components/Hero';
import ServiceCategories from '../components/ServiceCategories';
import RequestQuoteCTA from '../components/RequestQuoteCTA';
import HowItWorks from '../components/HowItWorks';
import Footer from '../components/Footer';
import AnnouncementPopup from '../components/AnnouncementPopup';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Toaster } from '../components/ui/toaster';
import { categoriesAPI } from '../services/api';
import { Star, MapPin, CheckCircle, Award, TrendingUp, FileText, UserCheck } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const HomePage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [categories, setCategories] = useState([]);
  const [featuredProviders, setFeaturedProviders] = useState([]);
  const [allProviders, setAllProviders] = useState([]);
  const [hasProviderProfile, setHasProviderProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [categoriesData, providersData] = await Promise.all([
          categoriesAPI.getAll(),
          axios.get(`${API_URL}/api/providers`)
        ]);
        
        setCategories(categoriesData);
        setAllProviders(providersData.data);
        
        // Filter featured/premium providers
        const featured = providersData.data.filter(p => p.featured || p.subscriptionTier === 'premium');
        setFeaturedProviders(featured.slice(0, 6));
        
        // Check if logged-in professional has provider profile
        if (isAuthenticated && user?.userType === 'professional' && user?.email) {
          const myProvider = providersData.data.find(p => p.email === user.email);
          setHasProviderProfile(!!myProvider);
        }
        
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isAuthenticated, user]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show public homepage for everyone (logged in or not)
  return (
    <div className="min-h-screen bg-white">
      <AnnouncementPopup />
      <Navbar />
      
      {/* Professional Setup Banner - PROMINENT ALERT - Only for logged-in professionals without provider profile */}
      {isAuthenticated && user?.userType === 'professional' && hasProviderProfile === false && (
        <section className="bg-gradient-to-r from-orange-600 to-red-600 border-b-4 border-red-700 shadow-2xl">
          <div className="max-w-7xl mx-auto px-4 py-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-start gap-4 text-white">
                <div className="bg-white/20 rounded-full p-3 animate-pulse">
                  <UserCheck className="h-8 w-8 text-white" />
                </div>
                <div>
                  <p className="text-xl font-bold text-white mb-1">⚠️ Action Required: Complete Your Business Profile</p>
                  <p className="text-orange-50 text-base">
                    Your professional account is incomplete. Create your business profile to:
                  </p>
                  <ul className="text-orange-50 text-sm mt-2 space-y-1">
                    <li>✓ Appear in search results and get discovered by customers</li>
                    <li>✓ Start receiving quote requests</li>
                    <li>✓ Build your online presence and grow your business</li>
                  </ul>
                </div>
              </div>
              <Button 
                onClick={() => navigate('/become-a-pro')}
                size="lg"
                className="bg-white text-orange-600 hover:bg-orange-50 font-bold shadow-lg hover:shadow-xl transition-all px-8 py-6 text-lg whitespace-nowrap"
              >
                Create Profile Now →
              </Button>
            </div>
          </div>
        </section>
      )}
      
      <Hero />
      <ServiceCategories categories={categories} />
      
      {/* Featured Premium Providers Section */}
      {featuredProviders.length > 0 && (
        <section className="py-16 bg-gradient-to-br from-orange-50 to-amber-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <div className="flex items-center justify-center gap-2 mb-3">
                <Award className="h-8 w-8 text-orange-600" />
                <h2 className="text-3xl font-bold text-gray-900">Featured Premium Professionals</h2>
              </div>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Top-rated professionals with verified credentials and outstanding service records
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {featuredProviders.map((provider) => (
                <Card 
                  key={provider.id} 
                  className="hover:shadow-2xl transition-all duration-300 cursor-pointer border-2 border-orange-200 bg-white"
                  onClick={() => navigate(`/provider/${provider.id}`)}
                >
                  <div className="relative">
                    <img
                      src={provider.image || 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400'}
                      alt={provider.name}
                      className="w-full h-48 object-cover"
                    />
                    <div className="absolute top-3 right-3">
                      <Badge className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white border-0 shadow-lg">
                        <Star className="h-3 w-3 mr-1 fill-white" />
                        FEATURED
                      </Badge>
                    </div>
                  </div>
                  
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-xl font-bold text-gray-900">{provider.name}</h3>
                      <div className="flex items-center">
                        <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                        <span className="ml-1 font-semibold text-gray-900">{provider.rating}</span>
                      </div>
                    </div>
                    
                    <div className="space-y-2 mb-4">
                      <p className="text-sm text-gray-600 flex items-center">
                        <MapPin className="h-4 w-4 mr-1 text-orange-600" />
                        {provider.location}
                      </p>
                      <p className="text-sm text-gray-600 line-clamp-2">{provider.description}</p>
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t">
                      <div className="flex items-center gap-2">
                        {provider.verified && (
                          <CheckCircle className="h-4 w-4 text-blue-600" />
                        )}
                        <span className="text-sm text-gray-600">{provider.reviewCount} reviews</span>
                      </div>
                      <Button 
                        size="sm"
                        className="bg-orange-600 hover:bg-orange-700 text-white"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/provider/${provider.id}`);
                        }}
                      >
                        View Profile
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center mt-10">
              <Button
                onClick={() => navigate('/providers')}
                variant="outline"
                size="lg"
                className="border-orange-600 text-orange-600 hover:bg-orange-50"
              >
                View All Professionals
              </Button>
            </div>
          </div>
        </section>
      )}
      
      <RequestQuoteCTA />
      <HowItWorks />
      <Footer />
      <Toaster />
    </div>
  );
};

export default HomePage;